import { Button } from '@mui/material';
import type { ConferenceFilter, SiteResponse } from '@on3/api';
import { On3IconCheck } from '@on3/icons';
import {
  groupFiveConferencesIds,
  powerFiveConferencesIds,
} from '@on3/ui-lib/utils/constants';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import { useCallback, useState } from 'react';

import styles from './SelectTeam.module.scss';

interface ISelectTeamProps {
  conferences?: ConferenceFilter[] | null;
  sites?: SiteResponse[] | null;
}
export const SelectTeam = ({ conferences, sites }: ISelectTeamProps) => {
  const channelSites = sites?.filter((site: SiteResponse) => site?.isChannel);
  const networkSites = sites
    ?.filter((site: SiteResponse) => site.isTeam && site.live && site.inIndex)
    .filter((s) => s.key !== 24);

  const allowedConferencesIds = [
    ...powerFiveConferencesIds,
    ...groupFiveConferencesIds,
  ];
  const powerFiveConferences = (conferences || []).filter((conference) =>
    powerFiveConferencesIds.includes(conference?.key || 0),
  );

  const groupFiveConferences = (conferences || []).filter((conference) =>
    groupFiveConferencesIds.includes(conference?.key || 0),
  );

  const displayConferences = [...powerFiveConferences, ...groupFiveConferences];
  const [selectedConference, setSelectConference] = useState('Network');
  const [filteredTeams, setFilterTeams] = useState(networkSites);

  const handleClick = useCallback(
    (conference: string | null) => (event: React.MouseEvent<HTMLElement>) => {
      if (conference) {
        setSelectConference(conference);
        if (conference === 'All') {
          setFilterTeams(channelSites);
        } else if (conference === 'Network') {
          setFilterTeams(networkSites);
        } else {
          setFilterTeams(
            channelSites?.filter(
              (site) => site.conference?.abbreviation === conference,
            ),
          );
        }
      }
    },
    [channelSites, networkSites],
  );
  const isAllConference = selectedConference === 'All';
  const isNetwork = selectedConference === 'Network';

  return (
    <div className={styles.selectTeamSection}>
      <div className={styles.selectTeamWrapper}>
        <header className={styles.header}>
          <Text className={styles.headerText} variant="h1">
            select your team
          </Text>
          <Text className={styles.subHeader} component="h2">
            Unlock the Best of College and High School Sports.
          </Text>
        </header>
        {!!displayConferences.length && (
          <div className={styles.nav}>
            <Button
              className={clsx(styles.button, isNetwork && styles.active)}
              onClick={handleClick('Network')}
            >
              <Text
                className={clsx(styles.team, {
                  [styles.active]: isNetwork,
                })}
                component="span"
              >
                On3 Network
              </Text>
            </Button>
            <Button
              className={clsx(styles.button, isAllConference && styles.active)}
              onClick={handleClick('All')}
            >
              <Text
                className={clsx(styles.team, {
                  [styles.active]: isAllConference,
                })}
                component="span"
              >
                All
              </Text>
            </Button>
            {displayConferences.map(({ key, abbreviation }) => {
              const isSelectedConference = selectedConference === abbreviation;

              return (
                <Button
                  className={clsx(
                    styles.button,
                    isSelectedConference && styles.active,
                  )}
                  key={key}
                  onClick={handleClick(abbreviation || '')}
                >
                  <Text
                    className={clsx(styles.team, {
                      [styles.active]: isSelectedConference,
                    })}
                    component="span"
                  >
                    {abbreviation}
                  </Text>
                </Button>
              );
            })}
          </div>
        )}
        {!!filteredTeams?.length && (
          <ul className={styles.teamList}>
            {filteredTeams
              ?.sort((a, b) => {
                const first = slugify(
                  a?.organization?.knownAs ?? a?.organization?.name,
                );
                const second = slugify(
                  b?.organization?.knownAs ?? b?.organization?.name,
                );

                if (first && second && first > second) {
                  return 1;
                }

                return -1;
              })
              .filter((team) =>
                allowedConferencesIds.includes(team?.conference?.key || 0),
              )
              .map((site: SiteResponse) => {
                if (!site?.organization) {
                  return null;
                }

                const isKsr =
                  site?.key === 395 || site?.key === 23 || site?.key === 24;
                const hasFanSite = !!site.siblingSiteKey;
                let joinLink = `/college/${site.slug}/join/`;

                if (isKsr) {
                  joinLink = `/sites/${site.slug}/join/`;
                } else if (hasFanSite) {
                  joinLink = `/teams/${site.slug}/join/`;
                }

                const orgName =
                  site.organization?.knownAs ?? site.organization?.name;

                return (
                  <li key={site.key}>
                    <a className={styles.teamItem} href={joinLink}>
                      <TeamAvatar
                        height={22}
                        slug={orgName}
                        src={getAssetUrl(site?.organization?.defaultAssetRes)}
                        width={22}
                      />
                      {orgName}
                    </a>
                  </li>
                );
              })}
          </ul>
        )}
        <Text className={styles.loginLink} component="span">
          Already have an account?
          <TextLink className={styles.linkText} href="/login/">
            Log in
          </TextLink>
        </Text>
        <div className={styles.benefitsWrapper}>
          <Text className={styles.benefitHeader} component="h3">
            Included with every membership
          </Text>

          <ul className={styles.benefitList}>
            <li className={styles.benefitItem}>
              <On3IconCheck className={styles.check} />
              <span className={styles.benefitText}>Limited Ads</span>
            </li>
            <li className={styles.benefitItem}>
              <On3IconCheck className={styles.check} />
              <span className={styles.benefitText}>
                Access to Message Boards
              </span>
            </li>
            <li className={styles.benefitItem}>
              <On3IconCheck className={styles.check} />
              <span className={styles.benefitText}>All premium content</span>
            </li>
            <li className={styles.benefitItem}>
              <On3IconCheck className={styles.check} />
              <span className={styles.benefitText}>IOS and Android App</span>
            </li>
            <li className={styles.benefitItem}>
              <On3IconCheck className={styles.check} />
              <span className={styles.benefitText}>Breaking News Alert</span>
            </li>
            <li className={styles.benefitItem}>
              <On3IconCheck className={styles.check} />
              <span className={styles.benefitText}>Access to On3 Network</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
